import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { consultationService } from '../services/consultationApi';
import ProfileIncompleteWarning from '../components/ProfileIncompleteWarning';
import AuroraLoadingOverlay from '../components/AuroraLoadingOverlay';
import NoTokensBanner from '../components/NoTokensBanner';
import { renderMarkdown } from '../utils/renderMarkdown';

const MAX_CHARS = 1500;

type Method = 'tarot' | 'sinastria' | 'numerologia' | 'compatibilidade';

const METHODS = [
  { id: 'tarot' as Method,          icon: '🃏', label: 'Tarot',          desc: 'Tiragem de Amor — Templo de Afrodite' },
  { id: 'sinastria' as Method,      icon: '🔮', label: 'Sinastria',      desc: 'Compatibilidade astral com alguém' },
  { id: 'numerologia' as Method,    icon: '🔢', label: 'Numerologia',    desc: 'Mapa afetivo pelos seus números' },
  { id: 'compatibilidade' as Method,icon: '💞', label: 'Compatibilidade',desc: 'Card de signos pra compartilhar' },
];

const AmorPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [method, setMethod] = useState<Method>('tarot');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const profile = (user as any)?.profile;
  const needsProfile = method === 'numerologia' && !profile?.birth_date;
  const missingFields = needsProfile ? ['birth_date'] : [];

  const handleSubmit = async () => {
    if (isLoading) return;

    if (method === 'tarot') {
      const q = question.trim() ? encodeURIComponent(question.trim()) : '';
      navigate(`/consultation?spread=templo-afrodite${q ? `&question=${q}` : ''}`);
      return;
    }
    if (method === 'sinastria') {
      navigate('/profile?tab=conexoes');
      return;
    }
    if (method === 'compatibilidade') {
      navigate('/compatibilidade');
      return;
    }

    // numerologia
    if (needsProfile) return;
    setIsLoading(true);
    setError(null);
    setAnswer('');
    try {
      const q = question.trim() || 'Analise meu mapa numerológico no contexto do amor e relacionamentos.';
      const result = await consultationService.askAurora(q, 'amor');
      setAnswer(result.general_interpretation);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Erro ao consultar Aurora Lyz');
    } finally {
      setIsLoading(false);
    }
  };

  const btnLabel = method === 'tarot' ? 'Iniciar Tiragem de Amor'
    : method === 'sinastria' ? 'Ver Conexões / Sinastria'
    : method === 'compatibilidade' ? 'Ver Card de Compatibilidade'
    : 'Consultar Aurora Lyz';

  return (
    <div className="min-h-screen bg-gradient-mystical text-white">
      <div className="flex items-center justify-between p-3 sm:p-6 border-b border-white/20">
        <Link to="/dashboard" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
          <ArrowLeft size={20} />
          <span>Voltar</span>
        </Link>
        <h1 className="text-xl font-mystical font-bold">Amor e Relacionamentos</h1>
        <div className="w-20" />
      </div>
      <AuroraLoadingOverlay isLoading={isLoading} message="Aurora está meditando..." />

      <div className="container mx-auto px-4 py-5 sm:py-10 max-w-2xl">
        <div className="text-center mb-8">
          <div className="text-3xl sm:text-5xl mb-3">💕</div>
          <h2 className="text-lg sm:text-2xl font-mystical font-bold mb-2">Amor e Relacionamentos</h2>
          <p className="text-white/55 text-sm leading-relaxed">Escolha o método de leitura e faça sua pergunta sobre a vida amorosa.</p>
          <p className="text-amber-400/80 text-xs mt-2">Consome 1 token por consulta</p>
        </div>

        <AnimatePresence mode="wait">
          {!answer && (
            <motion.div key="form" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>

              <div className="grid grid-cols-2 gap-3 mb-6">
                {METHODS.map(m => (
                  <button key={m.id} onClick={() => { setMethod(m.id); setError(null); }}
                    className="flex flex-col items-center gap-1.5 p-4 rounded-2xl text-center transition-all"
                    style={method === m.id
                      ? { background: 'rgba(244,63,94,0.15)', border: '1px solid rgba(244,63,94,0.45)' }
                      : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <span className="text-2xl">{m.icon}</span>
                    <span className="text-xs font-semibold text-white">{m.label}</span>
                    <span className="text-xs text-white/40 leading-tight">{m.desc}</span>
                  </button>
                ))}
              </div>

              {missingFields.length > 0 && <ProfileIncompleteWarning missingFields={missingFields} profileSection="dados" />}

              {method !== 'sinastria' && method !== 'compatibilidade' && missingFields.length === 0 && (
                <div className="relative mb-4">
                  <textarea
                    value={question}
                    onChange={e => setQuestion(e.target.value.slice(0, MAX_CHARS))}
                    placeholder={method === 'tarot' ? 'Opcional: faça uma pergunta sobre amor. Ex: Como está meu relacionamento atual?' : 'Opcional: faça uma pergunta numerológica sobre amor. Ex: O que meus números revelam sobre minha vida amorosa?'}
                    rows={4}
                    className="w-full border rounded-2xl px-5 py-4 focus:outline-none resize-none text-sm leading-relaxed"
                    style={{ background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.2)', color: 'white', caretColor: 'white' }}
                    disabled={isLoading}
                  />
                  <span className="absolute bottom-3 right-4 text-white/30 text-xs">{question.length}/{MAX_CHARS}</span>
                </div>
              )}

              {method === 'sinastria' && (
                <div className="mb-4 px-4 py-3 rounded-xl" style={{ background: 'rgba(192,38,211,0.08)', border: '1px solid rgba(192,38,211,0.25)' }}>
                  <p className="text-purple-300 text-sm">A Sinastria analisa a compatibilidade astral entre dois mapas natais. Você será redirecionado(a) para cadastrar ou visualizar suas conexões.</p>
                </div>
              )}

              {method === 'compatibilidade' && (
                <div className="mb-4 px-4 py-3 rounded-xl" style={{ background: 'rgba(244,63,94,0.08)', border: '1px solid rgba(244,63,94,0.25)' }}>
                  <p className="text-pink-300 text-sm">Escolha 2 signos e gere um card com família, amizade, sexo e amor — pronto pra baixar e compartilhar no Instagram/TikTok. Não usa tokens.</p>
                </div>
              )}

              <NoTokensBanner error={error} />

              <motion.button onClick={handleSubmit} disabled={isLoading || missingFields.length > 0}
                whileHover={!isLoading && missingFields.length === 0 ? { scale: 1.03 } : {}}
                whileTap={!isLoading && missingFields.length === 0 ? { scale: 0.97 } : {}}
                className="w-full py-3.5 rounded-2xl font-semibold text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #be185d, #f43f5e)' }}>
                {isLoading ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Aurora está consultando…</> : <><Sparkles size={18} />{btnLabel}</>}
              </motion.button>
            </motion.div>
          )}

          {answer && (
            <motion.div key="answer" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
              <div className="rounded-2xl p-4 sm:p-6 mb-6" style={{ background: 'linear-gradient(135deg, rgba(190,24,93,0.12) 0%, rgba(244,63,94,0.08) 100%)', border: '1px solid rgba(244,63,94,0.25)' }}>
                <div className="flex items-center gap-2 mb-3"><span className="text-xl">💕</span><span className="text-pink-300 font-mystical font-semibold text-sm">Aurora Lyz — Amor e Relacionamentos</span></div>
                <div className="text-white/90 text-sm leading-relaxed text-justify">{renderMarkdown(answer)}</div>
              </div>
              <button onClick={() => { setAnswer(''); setQuestion(''); setError(null); }} className="w-full py-3 rounded-2xl border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-all text-sm font-medium">Nova consulta</button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AmorPage;

